import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAnnouncements, announcementLimit } from '../firebase/announcementService';
import './ProfileAnnouncements.css';

// ── THE BANNER ABOVE A PROFILE ───────────────────────────────────────────────
// What a silver or gold designer pins to the top of their page: an
// advertisement, an offer, a piece of work worth leading with.
//
// ONE STILL OR A CAROUSEL, decided by the plan rather than by the count. Silver
// buys one image and gold buys three, so gold gets arrows and dots and an
// auto-advance while silver gets a plain picture — a carousel with one slide is
// chrome around nothing.
//
// TRIMMED TO THE PLAN AT RENDER TIME. A designer who drops from gold to silver
// still has three images stored until their next save, and this shows one of
// them. The plan is the authority on what is displayed, not what happens to be
// in the document.
//
// RENDERS NOTHING unless there is something to show — no heading, no empty
// frame, no placeholder. A profile without a banner should look like a profile,
// not like a profile with a hole in it.

const DWELL_MS = 6000;

export default function ProfileAnnouncements({ uid, plan, isShop = false }) {
  const [items, setItems] = useState([]);
  const [at, setAt] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const alive = useRef(true);

  const limit = announcementLimit(plan, isShop);

  useEffect(() => {
    alive.current = true;
    if (!uid || limit === 0) { setItems([]); return undefined; }
    fetchAnnouncements(uid).then(rows => {
      if (alive.current) setItems(rows.slice(0, limit));
    });
    return () => { alive.current = false; };
  }, [uid, limit]);

  const goTo = useCallback((i) => {
    const el = trackRef.current;
    if (!el || !items.length) return;
    const next = ((i % items.length) + items.length) % items.length;
    el.scrollTo({ left: el.clientWidth * next, behavior: 'smooth' });
    setAt(next);
  }, [items.length]);

  // Auto-advance, stopped while the pointer is over it and while the tab is
  // hidden — the same rules the dashboard banner follows.
  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const id = setInterval(() => { if (!document.hidden) goTo(at + 1); }, DWELL_MS);
    return () => clearInterval(id);
  }, [paused, at, items.length, goTo]);

  // A swipe moves the track without going through goTo, so the dots have to
  // read the position back or they stop agreeing with what is on screen.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setAt(Math.round(el.scrollLeft / (el.clientWidth || 1))));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(frame); };
  }, [items.length]);

  if (!items.length) return null;

  const many = items.length > 1;

  // An image that carries a link is an anchor; one that does not is a plain
  // picture. Wrapping a non-link in an <a href="#"> would put a control on the
  // page that goes nowhere.
  const Slide = ({ item }) => {
    const img = (
      <img
        className="pan-img"
        src={item.url}
        alt=""
        loading="lazy"
        decoding="async"
        width={item.w || undefined}
        height={item.h || undefined}
      />
    );
    return item.link
      ? <a className="pan-slide" href={item.link} target="_blank" rel="noopener noreferrer nofollow">{img}</a>
      : <div className="pan-slide">{img}</div>;
  };

  return (
    <div
      className={`pan${many ? ' pan--many' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="pan-track" ref={trackRef}>
        {items.map((item, i) => <Slide key={item.url.slice(-40) + i} item={item} />)}
      </div>

      {many && (
        <>
          <button type="button" className="pan-nav pan-nav--prev" onClick={() => goTo(at - 1)} aria-label="Previous">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5 8 12l7 7" /></svg>
          </button>
          <button type="button" className="pan-nav pan-nav--next" onClick={() => goTo(at + 1)} aria-label="Next">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
          </button>
          <div className="pan-dots">
            {items.map((item, i) => (
              <button
                key={`d${i}`}
                type="button"
                className={`pan-dot${i === at ? ' is-on' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
                aria-current={i === at}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
